import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { BedCreateEdit } from "./BedCreateEdit";
import { Bed } from "../../../../../interfaces/BedInterface";
import OkhatiDialog from "../../../../../components/Dialog/Dialog";
import { useBeds, useSoftDeleteBed } from "../../../../../hooks/query/useBeds";

// eslint-disable-next-line import/prefer-default-export
export function BedList(): React.ReactNode {
  const { mutate: deleteBed } = useSoftDeleteBed();
  const { data: beds = [], isLoading: isBedsLoading } = useBeds();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<number | null>(null);
  const [isCreateEditOpen, setIsCreateEditOpen] = useState(false);
  const [editingBed, setEditingBed] = useState<Bed | null>(null);

  const handleDelete = () => {
    if (deleteDialogOpen) {
      deleteBed(deleteDialogOpen, {
        onSuccess: () => {
          setDeleteDialogOpen(null);
        }
      });
    }
  };

  const handleCreateBeds = () => {
    setEditingBed(null);
    setIsCreateEditOpen(true);
  };

  const handleEdit = (bed: Bed) => {
    setEditingBed(bed);
    setIsCreateEditOpen(true);
  };

  const handleCreateEditSuccess = () => {
    setIsCreateEditOpen(false);
    setEditingBed(null);
  };

  const handleCreateEditClose = () => {
    setIsCreateEditOpen(false);
    setEditingBed(null);
  };

  if (isBedsLoading) {
    return <CircularProgress />;
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "auto"
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper"
        }}
      >
        <Typography sx={{ fontWeight: "500" }}>Manage Beds</Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleCreateBeds}>
          Add Bed
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderTop: 1,
          borderColor: "divider"
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "15%" }}>
                <Typography sx={{ fontWeight: "600", fontSize: "13px" }}>Bed No.</Typography>
              </TableCell>
              <TableCell sx={{ width: "25%" }}>
                <Typography sx={{ fontWeight: "600", fontSize: "13px" }}>Bed Code</Typography>
              </TableCell>
              <TableCell sx={{ width: "25%" }}>
                <Typography sx={{ fontWeight: "600", fontSize: "13px" }}>Bed Name</Typography>
              </TableCell>
              <TableCell sx={{ width: "20%" }}>
                <Typography sx={{ fontWeight: "600", fontSize: "13px" }}>Room</Typography>
              </TableCell>
              <TableCell sx={{ width: "15%" }}>
                <Typography sx={{ fontWeight: "600", fontSize: "13px", textAlign: "right" }}>
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          {beds.length > 0 && (
            <TableBody>
              {beds
                .filter((b) => b.roomId || b.room)
                .map((bed) => (
                  <TableRow key={bed.id} sx={{ height: "48px" }}>
                    <TableCell>
                      <Typography sx={{ fontSize: "13px" }}>{bed.number}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "13px" }}>{bed.code}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "13px" }}>{bed.customName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "13px" }}>{bed.room?.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 1
                        }}
                      >
                        <IconButton disabled={!bed.active} size="small">
                          <EditIcon
                            sx={{
                              cursor: "pointer",
                              fontSize: "16px",
                              color: "rgba(0, 0, 0, 0.54)"
                            }}
                            onClick={() => handleEdit(bed)}
                          />
                        </IconButton>
                        <IconButton disabled={!bed.active} size="small">
                          <DeleteIcon
                            sx={{ cursor: "pointer", fontSize: "16px", color: "red" }}
                            onClick={() => setDeleteDialogOpen(bed.id)}
                          />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>

      {deleteDialogOpen && (
        <OkhatiDialog
          title="Delete Bed"
          description="Are you sure you want to delete this bed?"
          next={handleDelete}
          cancel={() => setDeleteDialogOpen(null)}
          readMode={false}
        />
      )}

      {isCreateEditOpen && (
        <BedCreateEdit
          onClose={handleCreateEditClose}
          onSuccess={handleCreateEditSuccess}
          bed={editingBed}
        />
      )}
    </Box>
  );
}
